import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import adminApi from '../../services/adminApi';
import { useNavigate } from 'react-router-dom';

const ModernAdminHeader = ({ onToggleSidebar, onToggleMobileSidebar }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  // Charger les notifications
  useEffect(() => {
    loadNotifications();
    // Polling toutes les 30 secondes pour les nouvelles notifications
    const interval = setInterval(() => {
      loadNotifications();
    }, 30000); // 30 secondes

    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const notificationsData = await adminApi.getNotifications();
      console.log('Notifications data received:', notificationsData);

      // Gérer différents formats de réponse
      let notificationsList = [];
      if (Array.isArray(notificationsData)) {
        notificationsList = notificationsData;
      } else if (notificationsData && typeof notificationsData === 'object') {
        if (notificationsData.data && Array.isArray(notificationsData.data)) {
          notificationsList = notificationsData.data;
        } else if (notificationsData.success === false) {
          // Si l'API retourne une erreur, retourner un tableau vide
          console.warn('Notifications non disponibles:', notificationsData.message);
          notificationsList = [];
        }
      }

      console.log('Notifications list to display:', notificationsList.length, notificationsList);
      setNotifications(notificationsList);
      // Calculer le nombre de non lues de manière plus robuste
      const unread = notificationsList.filter(n => {
        const isRead = n.is_read;
        return isRead === false || isRead === 0 || isRead === '0' || !isRead;
      }).length;
      console.log('Unread count:', unread, 'Total:', notificationsList.length);
      setUnreadCount(unread);
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      // En cas d'erreur, initialiser avec un tableau vide
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await adminApi.markNotificationAsRead(notificationId);
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }

    // Rediriger vers la commande si c'est une notification de nouvelle commande
    if (notification.type === 'new_order' && notification.data?.order_id) {
      navigate(`/admin/orders`);
      setNotificationsOpen(false);
    } else {
      setNotificationsOpen(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      try {
        const results = await adminApi.search(searchQuery);
        console.log('Résultats de recherche:', results);
        // Ici vous pouvez rediriger vers une page de résultats ou afficher les résultats
        // Pour l'instant, on log juste les résultats
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
      }
    }
  };

  return (
    <header className="admin-header">
      <div className="admin-header-left">
        {/* Mobile menu button */}
        <button
          className="sidebar-toggle lg:hidden"
          onClick={onToggleMobileSidebar}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Desktop sidebar toggle */}
        <button
          className="sidebar-toggle hidden lg:block"
          onClick={onToggleSidebar}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Page title */}
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Tableau de Bord Admin
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Gestion du Marché Agricole
          </p>
        </div>
      </div>

      <div className="admin-header-right">
        {/* Search */}
        <form onSubmit={handleSearch} className="search-box">
          <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher produits, commandes, utilisateurs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="hidden">Rechercher</button>
        </form>

        {/* Notifications */}
        <div className="relative" style={{ position: 'relative' }}>
          <button
            className="relative sidebar-toggle"
            onClick={() => {
              console.log('Notification button clicked, unreadCount:', unreadCount);
              setNotificationsOpen(!notificationsOpen);
            }}
            style={{
              position: 'relative',
              padding: '8px',
              borderRadius: '8px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span
                className="absolute bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
                style={{
                  top: '-8px',
                  right: '-8px',
                  minWidth: '20px',
                  minHeight: '20px',
                  zIndex: 9999,
                  padding: '2px 6px',
                  fontSize: '11px',
                  lineHeight: '16px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  border: '2px solid white',
                  color: 'white'
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Dropdown des notifications */}
          {notificationsOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setNotificationsOpen(false)}
              />
              <div
                className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  minWidth: '320px'
                }}
              >
                <div
                  className="p-4 border-b border-gray-200 dark:border-gray-700"
                  style={{
                    backgroundColor: '#f9fafb',
                    borderBottom: '2px solid #e5e7eb'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <h3
                      className="text-lg font-semibold text-gray-900 dark:text-white"
                      style={{ fontSize: '16px', fontWeight: '600' }}
                    >
                      Notifications
                    </h3>
                    {unreadCount > 0 && (
                      <span
                        className="px-2 py-1 rounded-full text-xs font-bold"
                        style={{
                          backgroundColor: '#ef4444',
                          color: 'white',
                          fontSize: '11px',
                          padding: '4px 8px'
                        }}
                      >
                        {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="py-2">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <p className="text-sm">Aucune notification</p>
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notification) => {
                      const isUnread = !notification.is_read || notification.is_read === false || notification.is_read === 0 || notification.is_read === '0';
                      return (
                        <div
                          key={notification.id || `notif-${Math.random()}`}
                          className={`px-4 py-3 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-colors ${
                            isUnread ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800'
                          }`}
                          style={{
                            borderLeft: isUnread ? '3px solid #3b82f6' : '3px solid transparent'
                          }}
                          onClick={() => handleNotificationClick(notification)}
                          onMouseEnter={(e) => {
                            if (!isUnread) {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isUnread) {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4
                                  className="text-sm font-semibold text-gray-900 dark:text-white truncate"
                                  style={{
                                    fontWeight: isUnread ? '600' : '500',
                                    color: isUnread ? '#1e40af' : '#111827'
                                  }}
                                >
                                  {notification.title || 'Notification'}
                                </h4>
                                {isUnread && (
                                  <span
                                    className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"
                                    style={{ minWidth: '8px', minHeight: '8px' }}
                                  ></span>
                                )}
                              </div>
                              <p
                                className="text-sm text-gray-600 dark:text-gray-300 break-words"
                                style={{
                                  lineHeight: '1.5',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {notification.message || 'Aucun message'}
                              </p>
                              {notification.created_at && (
                                <p
                                  className="text-xs text-gray-400 mt-1"
                                  style={{ fontSize: '11px' }}
                                >
                                  {new Date(notification.created_at).toLocaleString('fr-FR', {
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {notifications.length > 10 && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-center">
                    <button
                      onClick={() => {
                        setNotificationsOpen(false);
                        navigate('/admin/orders');
                      }}
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      Voir toutes les notifications
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* User Menu */}
        <div className="relative">
          <button
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="hidden md:block text-left">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {user?.name || 'Administrateur'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {user?.email || 'admin@marche-agricole.com'}
              </div>
            </div>
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
              <div className="py-1">
                <a
                  href="/admin/profile"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Mon profil
                </a>
                <a
                  href="/admin/settings"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Paramètres
                </a>
                <hr className="my-1 border-gray-200 dark:border-gray-700" />
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Déconnexion
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ModernAdminHeader;
